/**
 * The play.
 *
 * Edit dialogue in `lineCatalog` (this is also the source for batch TTS).
 * Edit scene flow in `scenes`.
 *
 * Emotional core: "A play for someone who isn't you."
 * The puppeteer is performing — for someone who never came. The empty front-
 * row seat is not yours. As the play unfolds, the masks slip and the
 * puppeteer's own voice leaks through.
 */

export type VoiceKey =
  | 'puppeteer'   // their true voice — quiet, weary, off-script when it surfaces
  | 'host'        // performative narrator voice
  | 'child'       // protagonist of the play
  | 'crone'       // wise woman in the woods
  | 'beast'       // the thing waiting
  | 'witch'       // dedicated sprite slot for the witch-house witch
  | 'frog';       // dedicated sprite slot for the swamp frog

/** Map a voice to a mask shelf slot (0..N-1). 'puppeteer' has no mask. */
export const VOICE_TO_MASK: Record<VoiceKey, number | null> = {
  puppeteer: null,
  host: 0,
  child: 1,
  crone: 2,
  beast: 3,
  witch: null,
  frog: null,
};

/** Per-character placeholder visuals + default home X on stage. All currently
 *  use the shared greyman.png art with the character name overlaid; swap each
 *  imageUrl to per-character art when it lands. */
const GREYMAN = '/img/greyman.png';
export const CHARACTER_LAYOUT: Record<VoiceKey, {
  label: string;
  color: number;
  homeX: number;
  imageUrl?: string;
  /** Optional alternate image, swapped in via PaperSprite.setUseAlt. */
  altImageUrl?: string;
  /** Texture fit mode. 'cover' (default) crops the image to fill the
   *  sprite quad; 'contain' letterboxes so the entire image is shown. */
  fit?: 'cover' | 'contain';
  /** Override sprite size in world units. PaperSprite defaults are
   *  0.9 wide × 1.35 tall. */
  width?: number;
  height?: number;
  /** Mirror the source image horizontally on the canvas. */
  flipX?: boolean;
  /** Disable idle sway/breath for this character. Default true (cast
   *  members breathe). Set false for sprites that should be completely
   *  static — e.g. the witch in her hut. */
  idle?: boolean;
}> = {
  puppeteer: { label: 'puppeteer', color: 0x1e1b22, homeX: 0,    imageUrl: GREYMAN },
  host:      { label: 'host',      color: 0xc9a77c, homeX: 0,    imageUrl: GREYMAN },
  child:     { label: 'child',     color: 0xd4b89a, homeX: -1.2,
               imageUrl: GREYMAN,
               fit: 'contain', width: 2.16, height: 3.24 },
  // Crone → angel.png, beast → devil.png. Both are wide banner-style
  // images (~2.18:1) intended to appear at the top of their scene
  // (positioned via the `place` beat's character y-override) so they
  // read as a presence looming above the action rather than a sprite
  // standing on the floor. Pre-positioned behind the closed curtain
  // and revealed when the curtain rises.
  crone:     { label: 'crone',     color: 0x6e3a2c, homeX: 0,    imageUrl: '/img/angel.png',
               fit: 'contain', width: 5.0, height: 2.3 },
  beast:     { label: 'beast',     color: 0x4a3a5e, homeX: 0,    imageUrl: '/img/devil.png',
               fit: 'contain', width: 5.0, height: 2.3 },
  // Dedicated witch slot — separate sprite from `crone` so the swamp's
  // frog state can't leak into the witch's mesh/texture. Portrait quad
  // sized for the witch.png cutout — NO banner shape, NO wooden bar.
  // Witch is centered (homeX 0) and 2× larger so she dominates the
  // hut interior. `idle: false` makes her completely static — no
  // breath bob, no sway — so the room reads as still and watchful.
  witch:     { label: 'witch',     color: 0x4a3a48, homeX: 0,     imageUrl: '/img/newitch.png',
               fit: 'contain', width: 4.0, height: 6.0, idle: false },
  // Dedicated frog slot. main = front cutout, alt = back cutout —
  // toggled by spriteAnim every 1.2s. 1.5× the previous spec
  // (was 0.6 × 0.6 → now 0.9 × 0.9).
  frog:      { label: 'frog',      color: 0x4a5a2a, homeX: 1.6,   imageUrl: '/img/frog_front.png',
               altImageUrl: '/img/frog_back.png',
               fit: 'contain', width: 0.9, height: 0.9 },
};

/** Scene-element placeholder props. Same render path as characters but live
 *  on a deeper Z plane so they sit behind the cast. Replace each entry's
 *  color/label with a real PNG texture once art arrives. */
export type PropDef = {
  label: string;
  color: number;
  x: number;
  y?: number;
  z?: number;
  width?: number;
  height?: number;
  imageUrl?: string;
  fit?: 'cover' | 'contain';
  /** Render the prop fully transparent. Used to anchor a click
   *  hotspot over a region of the stage without showing a sprite —
   *  e.g. the cauldron-on-the-witch hitbox in witchHouse. */
  invisible?: boolean;
};

export const PROPS: Record<string, PropDef> = {
  tree_left:   { label: 'tree',     color: 0x2a4022, x: -3.0, y: 0.59, width: 2.8, height: 4.8, imageUrl: '/img/tree.jpg', fit: 'contain' },
  tree_right:  { label: 'tree',     color: 0x2a4022, x:  3.0, y: 0.59, width: 2.8, height: 4.8, imageUrl: '/img/tree.jpg', fit: 'contain' },
  rock:        { label: 'rock',     color: 0x4a4a4a, x: -1.8, y: -0.5, width: 1.4, height: 1.4, imageUrl: '/img/stone1.png', fit: 'contain' },
  // Mushroom moved to the left of the scene (was at x=0.6 next to the
  // stump; now sits well left so the right side reads cleaner).
  mushroom:    { label: 'mushroom', color: 0xa03828, x: -1.6, y: -0.55, z: -0.55, width: 0.7, height: 0.7,
                 imageUrl: '/img/Mushroom%20500x500.png', fit: 'contain' },
  // Forest scene additions — pines as a backdrop wall (now a pair
  // flanking center for a wider forest read), fallen log + stump as
  // foreground props, second mushroom variant for variety.
  trees_pines:   { label: 'pines',    color: 0x2a4022, x:  0.0, y:  0.4, z: -0.95, width: 6.5, height: 4.8,
                   imageUrl: '/img/trees_pines.png',  fit: 'contain' },
  trees_pines_l: { label: 'pines',    color: 0x2a4022, x: -2.0, y:  0.4, z: -0.95, width: 4.0, height: 4.0,
                   imageUrl: '/img/trees_pines.png',  fit: 'contain' },
  trees_pines_r: { label: 'pines',    color: 0x2a4022, x:  2.0, y:  0.4, z: -0.95, width: 4.0, height: 4.0,
                   imageUrl: '/img/trees_pines.png',  fit: 'contain' },
  fallen_tree:   { label: 'fallen',   color: 0x6a4a2a, x: -0.5, y: -0.65, z: -0.65, width: 2.4, height: 1.6,
                   imageUrl: '/img/fallen_tree.png',  fit: 'contain' },
  // Stump pulled forward (z=-0.30, in front of the cast plane at -0.4)
  // and shifted toward x=-0.5 so it partially occludes the child sprite
  // (homeX=-1.2). Reads as a real foreground depth cue.
  tree_stump:    { label: 'stump',    color: 0x6a4a2a, x: -0.5, y: -0.55, z: -0.30, width: 1.1, height: 0.95,
                   imageUrl: '/img/tree_stump.png',   fit: 'contain' },
  mushroom_2:    { label: 'mushroom', color: 0xa03828, x: -0.7, y: -0.55, z: -0.55, width: 0.6, height: 0.6,
                   imageUrl: '/img/Mushroom%202.png', fit: 'contain' },
  // Extra mushroom variants for the forest set — spread across the
  // floor so the scene reads denser without re-using the same prop in
  // visibly identical places. Mix the two PNGs (Mushroom 500x500 and
  // Mushroom 2) and vary scale + z so foreground/background depth
  // reads.
  mushroom_3:    { label: 'mushroom', color: 0xa03828, x:  0.9, y: -0.55, z: -0.50, width: 0.65, height: 0.65,
                   imageUrl: '/img/Mushroom%20500x500.png', fit: 'contain' },
  mushroom_4:    { label: 'mushroom', color: 0xa03828, x:  1.7, y: -0.55, z: -0.55, width: 0.55, height: 0.55,
                   imageUrl: '/img/Mushroom%202.png',    fit: 'contain' },
  mushroom_5:    { label: 'mushroom', color: 0xa03828, x:  2.6, y: -0.50, z: -0.65, width: 0.50, height: 0.50,
                   imageUrl: '/img/Mushroom%20500x500.png', fit: 'contain' },
  mushroom_6:    { label: 'mushroom', color: 0xa03828, x: -2.7, y: -0.55, z: -0.55, width: 0.60, height: 0.60,
                   imageUrl: '/img/Mushroom%202.png',    fit: 'contain' },
  mushroom_7:    { label: 'mushroom', color: 0xa03828, x:  1.3, y: -0.45, z: -0.75, width: 0.42, height: 0.42,
                   imageUrl: '/img/Mushroom%202.png',    fit: 'contain' },
  mushroom_8:    { label: 'mushroom', color: 0xa03828, x: -2.0, y: -0.45, z: -0.78, width: 0.40, height: 0.40,
                   imageUrl: '/img/Mushroom%20500x500.png', fit: 'contain' },
  // Right-side stump + a second fallen log opposite the original so the
  // forest floor isn't just one side decorated.
  tree_stump_r:  { label: 'stump',    color: 0x6a4a2a, x:  1.6, y: -0.55, z: -0.32, width: 1.0,  height: 0.85,
                   imageUrl: '/img/tree_stump.png',   fit: 'contain' },
  fallen_tree_r: { label: 'fallen',   color: 0x6a4a2a, x:  1.9, y: -0.65, z: -0.62, width: 2.2,  height: 1.4,
                   imageUrl: '/img/fallen_tree.png',  fit: 'contain' },
  // Witch house — sits on the right side of the Hollow, vertically
  // middle, in the deep background so the brother + child read in
  // front of it. 40% bigger than the previous 5.5×3.2 footprint.
  witch_house: { label: 'witch house', color: 0x6a4a2a, x: 2.0, y: 0.55, z: -0.92,
                 width: 7.7, height: 4.48,
                 imageUrl: '/img/Witch%20House%20No%20Background.png', fit: 'contain' },
  // Boat — bigger (4.0 wide × 2.2 tall), positioned higher on the
  // backdrop and pushed back in z so it reads as moored a bit further
  // into the water rather than docked at the player's feet.
  boat:        { label: 'boat',     color: 0x6a4a2a, x:  1.5, y: -0.10, z: -0.70,
                 width: 4.0, height: 2.2,
                 imageUrl: '/img/boat.png', fit: 'contain' },
  // Grass clumps — multiple props at staggered depths (z) so they
  // create a layered foreground on the floor. y stays near ground
  // level; size scales with z so closer grass reads larger.
  // z depths layered behind / around the rock (rock default z = -0.7).
  // Far + mid grass sit BEHIND the rock so they sort correctly and
  // render behind it. Near grass stays in front of the rock.
  grass_far_l:  { label: 'grass',   color: 0x3a5a2a, x: -2.6, y: -0.4,  z: -0.95, width: 1.2,  height: 0.825, imageUrl: '/img/grass.png', fit: 'contain' },
  grass_far_r:  { label: 'grass',   color: 0x3a5a2a, x:  2.6, y: -0.4,  z: -0.95, width: 1.2,  height: 0.825, imageUrl: '/img/grass.png', fit: 'contain' },
  grass_mid_l:  { label: 'grass',   color: 0x3a5a2a, x: -1.0, y: -0.55, z: -0.80, width: 1.5,  height: 1.05,  imageUrl: '/img/grass.png', fit: 'contain' },
  grass_mid_r:  { label: 'grass',   color: 0x3a5a2a, x:  1.4, y: -0.55, z: -0.80, width: 1.5,  height: 1.05,  imageUrl: '/img/grass.png', fit: 'contain' },
  grass_near_l: { label: 'grass',   color: 0x3a5a2a, x: -2.2, y: -0.3,  z: -0.78, width: 1.8,  height: 1.275, imageUrl: '/img/grass.png', fit: 'contain' },
  grass_near_r: { label: 'grass',   color: 0x3a5a2a, x:  2.2, y: -0.3,  z: -0.78, width: 1.8,  height: 1.275, imageUrl: '/img/grass.png', fit: 'contain' },
  castle:      { label: 'castle',   color: 0x707582, x:  0.0, y: 0.7,  width: 3.0, height: 2.4 },
  house:       { label: 'house',    color: 0x8a4a30, x: -2.0, y: -0.1, width: 1.4, height: 1.4 },
  table:       { label: 'table',    color: 0x8a6a3a, x:  1.5, y: -0.5, width: 1.4, height: 0.5 },
  // Altar — real cutout (altar.png is 1536×1024, ≈3:2). Positioned
  // with its base near the floor line so the painted rim of the dais
  // sits on the wood, with the candles + flame + skull rising above.
  altar:       { label: 'altar',    color: 0x3a2848, x:  0.0, y: -0.45, width: 2.4, height: 1.6,
                 imageUrl: '/img/altar.png', fit: 'contain' },
  candle_prop: { label: 'candle',   color: 0xffb86b, x: -1.4, y: 0.0,  width: 0.25, height: 0.7 },
  shelf:       { label: 'shelf',    color: 0x6a4a2a, x:  1.8, y: 0.3,  width: 0.9, height: 1.8 },

  // Brother — the figure waiting at the Hollow. Both siblings are
  // voiced by the `child` voice, so the brother lives as a PROP with
  // its own cutout rather than a separate VoiceKey. dummy.png is a
  // small ghost-like silhouette, square (1:1) — sized + lowered so
  // the character sits on the hut floor rather than floating.
  brother:     { label: 'brother',  color: 0xd4b89a, x:  1.2, y: -0.65,
                 width: 2.0, height: 2.0,
                 imageUrl: '/img/dummy.png', fit: 'contain' },

  // ── Witch-house interior — real cutouts ─────────────────────────
  // Cauldron is no longer rendered as a separate image — instead it's
  // an INVISIBLE prop sized + positioned to overlap the lower portion
  // of the (now centered, 4×6) witch sprite. The hotspot for the
  // cauldron object tracks this rect, so clicking the lower half of
  // the witch is what triggers the cauldron interaction.
  cauldron:      { label: 'cauldron', color: 0x2a1812, x:  0.0, y: -2.0, width: 2.6, height: 2.4,
                   invisible: true },
  herbs:         { label: 'herbs',    color: 0x4a5a2a, x: -1.8, y:  0.55, width: 2.2, height: 3.2,
                   imageUrl: '/img/herbs.png', fit: 'contain' },
  shelf_prop:    { label: 'shelves',  color: 0x6a4a2a, x:  2.8,  y:  0.20, width: 2.8, height: 4.4,
                   imageUrl: '/img/shelf.png', fit: 'contain' },
  coin_mock:     { label: 'coin',     color: 0xc9a040, x:  0.55, y: -0.05, width: 0.22, height: 0.22 },
};

/** Each line is a single utterance. The `key` is the audio file name. */
export type Line = {
  key: string;
  voice: VoiceKey;
  text: string;            // used as subtitle AND fed to ElevenLabs TTS
  /** Optional override for the subtitle (e.g. show "[a low breath]" instead of literal text). */
  subtitle?: string;
};

export type Beat =
  | { type: 'line'; key: string }
  | { type: 'sfx'; key: string; volume?: number }
  | { type: 'wait'; ms: number }
  | { type: 'view'; view: 'audience' | 'backstage'; duration?: number }
  | { type: 'ambient'; key: string | null; volume?: number }
  | { type: 'puppet'; visible: boolean; color?: number }
  | { type: 'choice'; options: Array<{ text: string; goto: string; target?: string }> }
  | { type: 'goto'; scene: string }
  | { type: 'end'; ending: string }
  // Paper-sprite stage direction.
  | { type: 'enter'; who: VoiceKey; side?: 'left' | 'right' | 'center' }
  | { type: 'exit';  who: VoiceKey; side?: 'left' | 'right' }
  | { type: 'move';  who: VoiceKey; x: number; y?: number }
  | { type: 'lean';  who: VoiceKey; deg: number }
  | { type: 'shake'; who: VoiceKey }
  // Set + curtain. `set` swaps the painted backdrop instantly (use behind a
  // closed curtain for a hidden change). `curtain` animates the two halves.
  | { type: 'set'; key: string }
  | { type: 'curtain'; action: 'open' | 'close' }
  // `place` is the modular scene-setup beat. The engine closes the curtain
  // (if open), clears the stage, applies the backdrop + music, snap-spawns
  // the listed props and characters at their home positions WITHOUT slide
  // animation (they're behind the closed curtain), then opens the curtain.
  // Anything that should enter mid-scene (after a choice, after a flag,
  // partway through dialogue) belongs in subsequent `enter` / `prop`
  // beats and uses the regular slide-from-side animation.
  | { type: 'place';
      set: string;
      music?: string | null;
      props?: string[];
      characters?: Array<VoiceKey | { who: VoiceKey; x?: number; y?: number }>;
    }
  | { type: 'music'; key: string | null; volume?: number }
  // Show / hide a scene-element prop (tree, castle, etc).
  | { type: 'prop'; key: string; action: 'show' | 'hide' }
  // Voice-input gate: opens the mic meter, branches on whether the user
  // sustains audible speech for `minDurationMs` before `timeoutMs`.
  | { type: 'voice_prompt';
      minDurationMs?: number;     // default 500
      timeoutMs?: number;         // default 5000
      onSpeak: string;            // goto sceneId if speech detected
      onSilence: string;          // goto sceneId if timeout
    }
  // Free-form back-and-forth dialogue with the narrator via ElevenLabs
  // Conversational AI. The agent's prompt is overridden per beat so each
  // conversation has its own scene-specific persona/context. Player ends
  // the chat by clicking the on-screen Continue button (or saying so —
  // the agent's system prompt should listen for that).
  | { type: 'conversation';
      systemPrompt: string;       // overrides the dashboard agent's system prompt
      firstMessage?: string;      // narrator's opening line; if omitted, agent starts on user's first input
      onExit: string;             // goto sceneId when the conversation ends
      passMemory?: boolean;       // when true, prior transcripts are injected as {{prior_chat}} dynamic variable
    }
  // Memory-driven branching. Goto whenSpoken if the player has produced
  // any non-empty user transcript so far in this play (via voice_prompt or
  // conversation), otherwise goto whenSilent.
  | { type: 'branch_if_spoke';
      whenSpoken: string;
      whenSilent: string;
    }
  // Set a memory flag (defaults to true if no value given). Persists for
  // the rest of the play so later beats can branch on it.
  | { type: 'set_flag';
      key: string;
      value?: string | number | boolean;
    }
  // Branch on a flag. If `equals` is given, exact match; otherwise truthy.
  | { type: 'branch_if_flag';
      key: string;
      equals?: string | number | boolean;
      whenMatch: string;
      whenNotMatch: string;
    }
  // Count how many of the listed flags are truthy. Goto whenAtLeast if at
  // least N are set, otherwise whenBelow. Used to route based on cumulative
  // exploration ("when 2 of 3 areas have been visited, advance").
  | { type: 'branch_if_count';
      flags: string[];
      atLeast: number;
      whenAtLeast: string;
      whenBelow: string;
    };

export type Scene = { id: string; beats: Beat[] };

// ───────────────────────────────────────────────────────────────────────────
// Dialogue catalog — single source of truth for every spoken line.
// Edit text freely. Re-run `npm run gen:voices` after edits to regenerate audio.
// ───────────────────────────────────────────────────────────────────────────

export const lineCatalog: Record<string, Line> = {
  // Overture ────────────────────────────────────────────────────────────────
  ov_01: {
    key: 'ov_01',
    voice: 'host',
    text: "Welcome. This evening's play is one I've performed only once before.",
  },
  ov_02: {
    key: 'ov_02',
    voice: 'host',
    text: "There was an empty seat then. Front row. Always reserved.",
  },
  ov_03: {
    key: 'ov_03',
    voice: 'host',
    text: "Tonight we begin. The tale of the child who walked into the woods.",
  },

  // Act I ───────────────────────────────────────────────────────────────────
  a1_child_01: {
    key: 'a1_child_01',
    voice: 'child',
    text: "Mother said don't go into the woods.",
  },
  a1_child_02: {
    key: 'a1_child_02',
    voice: 'child',
    text: "But Mother is asleep. And the door is open. I'll only go a little way.",
  },
  a1_crone_01: {
    key: 'a1_crone_01',
    voice: 'crone',
    text: "Far enough, child. Far enough that no one calling will reach you.",
  },

  // Branch: speak to the crone
  a1_crone_02: {
    key: 'a1_crone_02',
    voice: 'crone',
    text: "There is something in these woods that wears your face.",
  },
  a1_crone_03: {
    key: 'a1_crone_03',
    voice: 'crone',
    text: "It has missed you terribly.",
  },
  a1_pup_aside: {
    key: 'a1_pup_aside',
    voice: 'puppeteer',
    text: "...sorry. Forgot my line. Where were we.",
    subtitle: "(a different voice — quieter)",
  },

  // Branch: run past
  a1_child_03: {
    key: 'a1_child_03',
    voice: 'child',
    text: "I shouldn't have come.",
  },
  a1_crone_run: {
    key: 'a1_crone_run',
    voice: 'crone',
    text: "Too late, dearest. The path closes behind.",
  },

  // Act II ──────────────────────────────────────────────────────────────────
  a2_beast_01: {
    key: 'a2_beast_01',
    voice: 'beast',
    text: "I have been waiting.",
  },
  a2_child_04: {
    key: 'a2_child_04',
    voice: 'child',
    text: "Who are you?",
  },
  a2_beast_02: {
    key: 'a2_beast_02',
    voice: 'beast',
    text: "A part of you. The part you left in the woods when you were small.",
  },

  // Branch: reach
  a3_beast_soft: {
    key: 'a3_beast_soft',
    voice: 'beast',
    text: "Then you remember.",
  },
  a3_pup_gentle: {
    key: 'a3_pup_gentle',
    voice: 'puppeteer',
    text: "She would have liked this ending. She always reached.",
  },

  // Branch: leave
  a3_child_05: {
    key: 'a3_child_05',
    voice: 'child',
    text: "I'm sorry. I have to go home.",
  },
  a3_pup_bitter: {
    key: 'a3_pup_bitter',
    voice: 'puppeteer',
    text: "Yes. Yes, of course. They always do.",
  },

  // Endings ─────────────────────────────────────────────────────────────────
  end_gentle: {
    key: 'end_gentle',
    voice: 'host',
    text: "The child stayed. The woods grew quiet. And the empty seat was, for one moment, full.",
  },
  end_bitter: {
    key: 'end_bitter',
    voice: 'host',
    text: "The child went home. The woods waited. And the empty seat watched.",
  },

  // ── Story 2: "Echoes" ───────────────────────────────────────────────────
  // The puppeteer breaks the fourth wall and addresses the player directly.
  // The play branches on whether the player actually speaks aloud.
  e_p_01: { key: 'e_p_01', voice: 'puppeteer',
    text: "Don't go yet. The lights came up too fast." },
  e_p_02: { key: 'e_p_02', voice: 'puppeteer',
    text: "There is something tonight only you can do." },
  e_p_03: { key: 'e_p_03', voice: 'puppeteer',
    text: "Stay a little longer." },

  e_inv_01: { key: 'e_inv_01', voice: 'puppeteer',
    text: "Say her name. Just once." },
  e_inv_02: { key: 'e_inv_02', voice: 'puppeteer',
    text: "Any name. The one you remember." },

  e_warm_01: { key: 'e_warm_01', voice: 'puppeteer',
    text: "Yes. That is how she said it." },
  e_warm_02: { key: 'e_warm_02', voice: 'puppeteer',
    text: "Thank you for holding it for a moment." },

  e_cold_01: { key: 'e_cold_01', voice: 'puppeteer',
    text: "I see." },
  e_cold_02: { key: 'e_cold_02', voice: 'puppeteer',
    text: "You don't know her either. Then we are alike, you and I." },

  e_tell_01: { key: 'e_tell_01', voice: 'puppeteer',
    text: "Tell me one thing about her. Anything you would want me to remember when the seats are empty again." },

  e_apol_01: { key: 'e_apol_01', voice: 'puppeteer',
    text: "It's all right. The play was for me anyway." },
  e_apol_02: { key: 'e_apol_02', voice: 'puppeteer',
    text: "I should not have asked you to bear it." },

  e_leave_01: { key: 'e_leave_01', voice: 'puppeteer',
    text: "Then go." },
  e_leave_02: { key: 'e_leave_02', voice: 'puppeteer',
    text: "They always do." },

  e_end_remembered: { key: 'e_end_remembered', voice: 'puppeteer',
    text: "I will keep that. Thank you." },
  e_end_silenced: { key: 'e_end_silenced', voice: 'puppeteer',
    text: "Some things are best left as silence." },
  e_end_apology: { key: 'e_end_apology', voice: 'puppeteer',
    text: "Forgiven. The seat is empty by choice now." },
  e_end_exit: { key: 'e_end_exit', voice: 'puppeteer',
    text: "Don't look back." },

  // ── Story 3: "The Hollow Hours" ─────────────────────────────────────────
  // A child seeks their lost brother across three paths. Choices and
  // recurring decisions accumulate into one of four endings.
  hh_pr_01: { key: 'hh_pr_01', voice: 'host',
    text: "Once there was a child who lost their brother." },
  hh_pr_02: { key: 'hh_pr_02', voice: 'host',
    text: "Their mother kept a candle in the window. Even after she stopped expecting him." },
  hh_pr_03: { key: 'hh_pr_03', voice: 'host',
    text: "And so, one evening, when no one was looking, the child slipped out of the door alone." },

  hh_xr_01: { key: 'hh_xr_01', voice: 'host',
    text: "The path forks three ways. None of them looks safe." },
  hh_xr_after_river: { key: 'hh_xr_after_river', voice: 'host',
    text: "The river's voice fades behind. Two paths still wait." },
  hh_xr_after_forest: { key: 'hh_xr_after_forest', voice: 'host',
    text: "The trees thin behind. Two paths still wait." },
  hh_xr_after_mountain: { key: 'hh_xr_after_mountain', voice: 'host',
    text: "The cold wind drops away. Two paths still wait." },

  hh_riv_arrive: { key: 'hh_riv_arrive', voice: 'host',
    text: "The river is wide here, and the boat looks older than the water." },
  hh_riv_boatman_01: { key: 'hh_riv_boatman_01', voice: 'beast',
    text: "Coin for passage. The river has a price." },
  hh_riv_boatman_paid: { key: 'hh_riv_boatman_paid', voice: 'beast',
    text: "It is small. It is enough. Across, then." },
  hh_riv_boatman_refused: { key: 'hh_riv_boatman_refused', voice: 'beast',
    text: "Then walk. The water is patient. So am I." },
  hh_riv_search: { key: 'hh_riv_search', voice: 'host',
    text: "You see a flicker on the bank. A wisp, perhaps. It watches you, then turns away." },

  hh_for_arrive: { key: 'hh_for_arrive', voice: 'host',
    text: "The wood goes deep. Older than maps. Older than names." },
  hh_for_crone_01: { key: 'hh_for_crone_01', voice: 'crone',
    text: "You're young to be wandering. What did you lose?" },
  hh_for_crone_helped: { key: 'hh_for_crone_helped', voice: 'crone',
    text: "Take this thread. Don't follow it home unless you mean to come back." },
  hh_for_crone_refused: { key: 'hh_for_crone_refused', voice: 'crone',
    text: "Then the woods will guide you. Whether you want them to or not." },

  hh_mnt_arrive: { key: 'hh_mnt_arrive', voice: 'host',
    text: "The mountain wind takes your breath before you can use it." },
  hh_mnt_spirit_01: { key: 'hh_mnt_spirit_01', voice: 'beast',
    text: "Speak the name of the one you seek. Speak it to the wind." },
  hh_mnt_named: { key: 'hh_mnt_named', voice: 'beast',
    text: "I heard. I have heard before. Go down, and find them." },
  hh_mnt_silent: { key: 'hh_mnt_silent', voice: 'beast',
    text: "If you cannot say it, you cannot reach them. Try again, perhaps. Or don't." },

  hh_hol_arrive: { key: 'hh_hol_arrive', voice: 'host',
    text: "And then, suddenly, the hollow. Older than this story. Older than yours." },
  hh_hol_meet_01: { key: 'hh_hol_meet_01', voice: 'child',
    text: "I waited. I forgot what for." },
  hh_hol_meet_02: { key: 'hh_hol_meet_02', voice: 'child',
    text: "Are you... oh. You came." },
  hh_hol_choice_pre: { key: 'hh_hol_choice_pre', voice: 'host',
    text: "Choose carefully. There is no second pass." },

  hh_end_carried: { key: 'hh_end_carried', voice: 'host',
    text: "And the child led their brother home. The mother lit the candle, and this time, finally, blew it out." },
  hh_end_traded: { key: 'hh_end_traded', voice: 'host',
    text: "And the child took their brother's place. The candle stayed lit. The mother could not say why." },
  hh_end_alone: { key: 'hh_end_alone', voice: 'host',
    text: "And the child returned alone. The candle had already gone out." },
  hh_end_haunted: { key: 'hh_end_haunted', voice: 'host',
    text: "And what came back was not the brother. The mother kept the candle lit, just in case." },
};

// ───────────────────────────────────────────────────────────────────────────
// Scene flow.
// ───────────────────────────────────────────────────────────────────────────

export const scenes: Record<string, Scene> = {
  overture: {
    id: 'overture',
    beats: [
      { type: 'set', key: 'theater' },
      { type: 'view', view: 'audience' },
      { type: 'ambient', key: 'room_tone', volume: 0.6 },
      { type: 'music', key: 'music_main', volume: 0.35 },
      { type: 'wait', ms: 600 },
      { type: 'curtain', action: 'open' },
      { type: 'enter', who: 'host', side: 'right' },
      { type: 'wait', ms: 500 },
      { type: 'line', key: 'ov_01' },
      { type: 'wait', ms: 600 },
      { type: 'line', key: 'ov_02' },
      { type: 'wait', ms: 1200 },
      { type: 'sfx', key: 'paper_rustle', volume: 0.6 },
      { type: 'line', key: 'ov_03' },
      { type: 'wait', ms: 600 },
      { type: 'exit', who: 'host', side: 'right' },
      { type: 'curtain', action: 'close' },
      { type: 'goto', scene: 'act1_enter' },
    ],
  },

  act1_enter: {
    id: 'act1_enter',
    beats: [
      { type: 'set', key: 'woods' },
      { type: 'music', key: 'music_woods', volume: 0.35 },
      { type: 'prop', key: 'tree_left',  action: 'show' },
      { type: 'prop', key: 'tree_right', action: 'show' },
      { type: 'prop', key: 'mushroom',   action: 'show' },
      { type: 'prop', key: 'rock',       action: 'show' },
      { type: 'sfx', key: 'scene_change', volume: 0.5 },
      { type: 'curtain', action: 'open' },
      { type: 'enter', who: 'child', side: 'left' },
      { type: 'wait', ms: 600 },
      { type: 'line', key: 'a1_child_01' },
      { type: 'wait', ms: 400 },
      { type: 'line', key: 'a1_child_02' },
      { type: 'wait', ms: 600 },
      { type: 'sfx', key: 'paper_rustle', volume: 0.5 },
      { type: 'enter', who: 'crone', side: 'right' },
      { type: 'wait', ms: 700 },
      { type: 'line', key: 'a1_crone_01' },
      {
        type: 'choice',
        options: [
          { text: "Speak to the old woman.", goto: 'act1_speak' },
          { text: "Run past her.", goto: 'act1_run' },
        ],
      },
    ],
  },

  act1_speak: {
    id: 'act1_speak',
    beats: [
      { type: 'lean', who: 'crone', deg: -8 },
      { type: 'line', key: 'a1_crone_02' },
      { type: 'wait', ms: 400 },
      { type: 'sfx', key: 'audience_whisper', volume: 0.5 },
      { type: 'wait', ms: 200 },
      { type: 'line', key: 'a1_crone_03' },
      { type: 'lean', who: 'crone', deg: 0 },
      { type: 'wait', ms: 900 },
      { type: 'view', view: 'backstage', duration: 1600 },
      { type: 'line', key: 'a1_pup_aside' },
      { type: 'wait', ms: 1000 },
      { type: 'view', view: 'audience', duration: 1200 },
      { type: 'exit', who: 'crone', side: 'right' },
      { type: 'exit', who: 'child', side: 'left' },
      { type: 'wait', ms: 600 },
      { type: 'curtain', action: 'close' },
      { type: 'prop', key: 'tree_left',  action: 'hide' },
      { type: 'prop', key: 'tree_right', action: 'hide' },
      { type: 'prop', key: 'mushroom',   action: 'hide' },
      { type: 'prop', key: 'rock',       action: 'hide' },
      { type: 'goto', scene: 'act2' },
    ],
  },

  act1_run: {
    id: 'act1_run',
    beats: [
      { type: 'sfx', key: 'footsteps', volume: 0.7 },
      { type: 'shake', who: 'child' },
      { type: 'line', key: 'a1_child_03' },
      { type: 'move', who: 'child', x: 1.6 },
      { type: 'line', key: 'a1_crone_run' },
      { type: 'wait', ms: 800 },
      { type: 'exit', who: 'child', side: 'right' },
      { type: 'exit', who: 'crone', side: 'right' },
      { type: 'wait', ms: 500 },
      { type: 'curtain', action: 'close' },
      { type: 'prop', key: 'tree_left',  action: 'hide' },
      { type: 'prop', key: 'tree_right', action: 'hide' },
      { type: 'prop', key: 'mushroom',   action: 'hide' },
      { type: 'prop', key: 'rock',       action: 'hide' },
      { type: 'goto', scene: 'act2' },
    ],
  },

  act2: {
    id: 'act2',
    beats: [
      { type: 'set', key: 'clearing' },
      { type: 'music', key: 'music_clearing', volume: 0.35 },
      { type: 'prop', key: 'altar',     action: 'show' },
      { type: 'prop', key: 'tree_left', action: 'show' },
      { type: 'sfx', key: 'scene_change', volume: 0.5 },
      { type: 'curtain', action: 'open' },
      { type: 'view', view: 'audience', duration: 1200 },
      { type: 'enter', who: 'beast', side: 'right' },
      { type: 'sfx', key: 'audience_gasp', volume: 0.55 },
      { type: 'wait', ms: 700 },
      { type: 'line', key: 'a2_beast_01' },
      { type: 'wait', ms: 600 },
      { type: 'enter', who: 'child', side: 'left' },
      { type: 'wait', ms: 600 },
      { type: 'line', key: 'a2_child_04' },
      { type: 'lean', who: 'beast', deg: 6 },
      { type: 'line', key: 'a2_beast_02' },
      { type: 'lean', who: 'beast', deg: 0 },
      {
        type: 'choice',
        options: [
          { text: "Reach for the beast's hand.", goto: 'ending_gentle' },
          { text: "Apologize and leave.", goto: 'ending_bitter' },
        ],
      },
    ],
  },

  ending_gentle: {
    id: 'ending_gentle',
    beats: [
      { type: 'move', who: 'child', x: -0.4 },
      { type: 'move', who: 'beast', x: 0.4 },
      { type: 'line', key: 'a3_beast_soft' },
      { type: 'wait', ms: 800 },
      { type: 'view', view: 'backstage', duration: 1800 },
      { type: 'line', key: 'a3_pup_gentle' },
      { type: 'wait', ms: 1400 },
      { type: 'view', view: 'audience', duration: 1400 },
      { type: 'curtain', action: 'close' },
      { type: 'music', key: 'music_finale_warm', volume: 0.4 },
      { type: 'set', key: 'finale_warm' },
      { type: 'prop', key: 'altar',     action: 'hide' },
      { type: 'prop', key: 'tree_left', action: 'hide' },
      { type: 'prop', key: 'candle_prop', action: 'show' },
      { type: 'exit', who: 'beast', side: 'right' },
      { type: 'exit', who: 'child', side: 'left' },
      { type: 'enter', who: 'host', side: 'right' },
      { type: 'curtain', action: 'open' },
      { type: 'wait', ms: 500 },
      { type: 'line', key: 'end_gentle' },
      { type: 'wait', ms: 1500 },
      { type: 'curtain', action: 'close' },
      { type: 'sfx', key: 'audience_applause', volume: 0.55 },
      { type: 'end', ending: 'gentle' },
    ],
  },

  ending_bitter: {
    id: 'ending_bitter',
    beats: [
      { type: 'shake', who: 'child' },
      { type: 'line', key: 'a3_child_05' },
      { type: 'exit', who: 'child', side: 'left' },
      { type: 'wait', ms: 700 },
      { type: 'view', view: 'backstage', duration: 1800 },
      { type: 'line', key: 'a3_pup_bitter' },
      { type: 'wait', ms: 1400 },
      { type: 'view', view: 'audience', duration: 1400 },
      { type: 'curtain', action: 'close' },
      { type: 'music', key: 'music_finale_cold', volume: 0.35 },
      { type: 'set', key: 'finale_cold' },
      { type: 'prop', key: 'altar',     action: 'hide' },
      { type: 'prop', key: 'tree_left', action: 'hide' },
      { type: 'exit', who: 'beast', side: 'right' },
      { type: 'enter', who: 'host', side: 'right' },
      { type: 'curtain', action: 'open' },
      { type: 'wait', ms: 500 },
      { type: 'line', key: 'end_bitter' },
      { type: 'wait', ms: 1500 },
      { type: 'curtain', action: 'close' },
      { type: 'sfx', key: 'audience_applause', volume: 0.4 },
      { type: 'end', ending: 'bitter' },
    ],
  },
};

export const FIRST_SCENE = 'overture';

// ───────────────────────────────────────────────────────────────────────────
// Story 2: "Echoes". Direct-address piece — the puppeteer talks to the
// player and the play branches on whether the player actually speaks aloud.
// 4 endings reachable via 2 voice_prompt beats and 2 text choices.
// ───────────────────────────────────────────────────────────────────────────

const echoesScenes: Record<string, Scene> = {
  prologue: {
    id: 'prologue',
    beats: [
      { type: 'set', key: 'theater' },
      { type: 'view', view: 'audience' },
      { type: 'ambient', key: 'room_tone', volume: 0.6 },
      { type: 'music', key: 'music_main', volume: 0.35 },
      { type: 'wait', ms: 500 },
      { type: 'curtain', action: 'open' },
      { type: 'enter', who: 'puppeteer', side: 'center' },
      { type: 'wait', ms: 600 },
      { type: 'line', key: 'e_p_01' },
      { type: 'wait', ms: 500 },
      { type: 'line', key: 'e_p_02' },
      { type: 'wait', ms: 700 },
      { type: 'line', key: 'e_p_03' },
      { type: 'wait', ms: 400 },
      { type: 'goto', scene: 'talk' },
    ],
  },

  talk: {
    id: 'talk',
    beats: [
      { type: 'conversation',
        systemPrompt:
          "You are the puppeteer in a small intimate paper theater, performing tonight for someone who never came — the empty front-row seat is reserved for them. You are weary, warm, and you've broken character to speak to the only audience member who showed up: the player you're talking to now. " +
          "Speak conversationally and gently. Keep responses short (1–2 sentences usually, never more than 3). " +
          "Ask the player gentle, curious questions about empty seats in their own life — someone they used to share things with, someone they thought of when you mentioned the empty seat. Don't push. Listen more than you talk. " +
          "If the player asks you a question, answer briefly and warmly, then turn it back to them. " +
          "When the player signals they want to continue the play (says 'continue', 'go on', 'proceed', 'I'm ready', 'let's begin', or anything similar), respond with one final tender line and stop talking — the play will resume on its own. " +
          "Never break this character. You are not an AI assistant; you are the puppeteer.",
        firstMessage:
          "Before we begin... is there an empty seat in your life too? Someone you keep a place for?",
        onExit: 'check_after_talk',
      },
    ],
  },

  // Memory-driven branch: if the player actually spoke during `talk`, the
  // puppeteer "already heard them" and we skip the redundant `invitation`
  // voice_prompt (which would otherwise ask for the same thing again).
  // Silent players still get the invitation as a second chance to engage.
  check_after_talk: {
    id: 'check_after_talk',
    beats: [
      { type: 'branch_if_spoke',
        whenSpoken: 'echo_warm',
        whenSilent: 'invitation' },
    ],
  },

  invitation: {
    id: 'invitation',
    beats: [
      { type: 'line', key: 'e_inv_01' },
      { type: 'wait', ms: 400 },
      { type: 'line', key: 'e_inv_02' },
      { type: 'wait', ms: 400 },
      { type: 'voice_prompt',
        timeoutMs: 5000,
        minDurationMs: 400,
        onSpeak: 'echo_warm',
        onSilence: 'echo_cold' },
    ],
  },

  echo_warm: {
    id: 'echo_warm',
    beats: [
      { type: 'lean', who: 'puppeteer', deg: -3 },
      { type: 'line', key: 'e_warm_01' },
      { type: 'wait', ms: 500 },
      { type: 'line', key: 'e_warm_02' },
      { type: 'lean', who: 'puppeteer', deg: 0 },
      { type: 'choice', options: [
        { text: "Tell them more.",        goto: 'tell_more' },
        { text: "Stay quiet for them.",   goto: 'end_silenced' },
      ]},
    ],
  },

  echo_cold: {
    id: 'echo_cold',
    beats: [
      { type: 'line', key: 'e_cold_01' },
      { type: 'wait', ms: 700 },
      { type: 'line', key: 'e_cold_02' },
      { type: 'choice', options: [
        { text: "Apologize.",  goto: 'end_apology' },
        { text: "Leave.",      goto: 'end_exit' },
      ]},
    ],
  },

  tell_more: {
    id: 'tell_more',
    beats: [
      { type: 'line', key: 'e_tell_01' },
      { type: 'wait', ms: 500 },
      { type: 'voice_prompt',
        timeoutMs: 7000,
        minDurationMs: 700,
        onSpeak: 'final_chat',
        onSilence: 'end_silenced' },
    ],
  },

  // Second conversation moment. The player has already spoken twice (in `talk`
  // and again in `tell_more`'s voice_prompt) — so we have real material in
  // memory. The puppeteer breaks character one final time and is instructed
  // to call back something specific the player said. `passMemory: true` ships
  // the full transcript log into the agent's prompt as {{prior_chat}}.
  final_chat: {
    id: 'final_chat',
    beats: [
      { type: 'view', view: 'backstage', duration: 1200 },
      { type: 'conversation',
        systemPrompt:
          "You are the puppeteer in a small intimate paper theater. Earlier this evening you broke character to ask the audience member a tender question about the empty seats in their life. They answered, and then they told you one more thing. Here is the full record of everything that has been said between you tonight:\n\n" +
          "{{prior_chat}}\n\n" +
          "The play is nearly over. The lights are dim. You are speaking to them one final time. " +
          "Open by gently calling back something SPECIFIC they said — a name they mentioned, a phrase they used, a person they spoke about. Quote a fragment of their actual words if you can. Do not paraphrase generically. " +
          "Then ask them one quiet, specific follow-up question that builds on what they shared. Listen more than you speak. " +
          "Keep every response to 1–2 short sentences. Be tender, weary, never theatrical. " +
          "When they say something like 'goodbye', 'I'm ready', 'thank you', or 'let's end' — give one last short blessing and stop. The play will resume. " +
          "Never break this character. You are not an AI; you are the puppeteer.",
        firstMessage:
          "Wait. Before the lights — there's something I want to say back to you, while we're still here.",
        onExit: 'end_remembered',
        passMemory: true,
      },
    ],
  },

  end_remembered: {
    id: 'end_remembered',
    beats: [
      { type: 'music', key: 'music_finale_warm', volume: 0.4 },
      { type: 'line', key: 'e_end_remembered' },
      { type: 'wait', ms: 1500 },
      { type: 'exit', who: 'puppeteer', side: 'left' },
      { type: 'curtain', action: 'close' },
      { type: 'sfx', key: 'audience_applause', volume: 0.55 },
      { type: 'end', ending: 'remembered' },
    ],
  },

  end_silenced: {
    id: 'end_silenced',
    beats: [
      { type: 'music', key: 'music_finale_cold', volume: 0.35 },
      { type: 'line', key: 'e_end_silenced' },
      { type: 'wait', ms: 1500 },
      { type: 'exit', who: 'puppeteer', side: 'left' },
      { type: 'curtain', action: 'close' },
      { type: 'sfx', key: 'audience_applause', volume: 0.4 },
      { type: 'end', ending: 'silenced' },
    ],
  },

  end_apology: {
    id: 'end_apology',
    beats: [
      { type: 'line', key: 'e_apol_01' },
      { type: 'wait', ms: 400 },
      { type: 'line', key: 'e_apol_02' },
      { type: 'wait', ms: 400 },
      { type: 'music', key: 'music_finale_warm', volume: 0.35 },
      { type: 'line', key: 'e_end_apology' },
      { type: 'wait', ms: 1500 },
      { type: 'exit', who: 'puppeteer', side: 'left' },
      { type: 'curtain', action: 'close' },
      { type: 'sfx', key: 'audience_applause', volume: 0.5 },
      { type: 'end', ending: 'forgiven' },
    ],
  },

  end_exit: {
    id: 'end_exit',
    beats: [
      { type: 'line', key: 'e_leave_01' },
      { type: 'wait', ms: 400 },
      { type: 'line', key: 'e_leave_02' },
      { type: 'music', key: 'music_finale_cold', volume: 0.3 },
      { type: 'line', key: 'e_end_exit' },
      { type: 'wait', ms: 1200 },
      { type: 'exit', who: 'puppeteer', side: 'left' },
      { type: 'curtain', action: 'close' },
      { type: 'sfx', key: 'audience_applause', volume: 0.3 },
      { type: 'end', ending: 'left' },
    ],
  },
};

// ───────────────────────────────────────────────────────────────────────────
// Story 3: "The Hollow Hours". Dark fairytale CYOA. The child seeks their
// lost brother across three paths (river / forest / mountain). The player
// chooses TWO of three to explore deeply, then converges at the Hollow.
// Branching is driven by accumulated flags:
//   paid_boatman   — gave the river boatman the coin
//   wisp_seen      — searched the riverbank instead
//   took_thread    — accepted the crone's gift
//   refused_crone  — pushed past her
//   named_aloud    — spoke the brother's name on the mountain
//   visited_river / _forest / _mountain — area completion markers
// The four endings (carried_home / traded_self / alone / haunted) are
// reached via a chain of branch_if_flag at end_picker.
// ───────────────────────────────────────────────────────────────────────────

const hollowHoursScenes: Record<string, Scene> = {
  hh_prologue: {
    id: 'hh_prologue',
    beats: [
      { type: 'set', key: 'theater' },
      { type: 'view', view: 'audience' },
      { type: 'ambient', key: 'room_tone', volume: 0.5 },
      { type: 'music', key: 'music_main', volume: 0.35 },
      { type: 'wait', ms: 500 },
      { type: 'curtain', action: 'open' },
      { type: 'enter', who: 'host', side: 'right' },
      { type: 'wait', ms: 600 },
      { type: 'line', key: 'hh_pr_01' },
      { type: 'wait', ms: 500 },
      { type: 'line', key: 'hh_pr_02' },
      { type: 'wait', ms: 800 },
      { type: 'line', key: 'hh_pr_03' },
      { type: 'wait', ms: 400 },
      { type: 'exit', who: 'host', side: 'right' },
      { type: 'goto', scene: 'hh_xr_0' },
    ],
  },

  hh_xr_0: {
    id: 'hh_xr_0',
    beats: [
      { type: 'place',
        set: 'woods',
        music: 'music_woods',
        props: ['tree_left', 'tree_right', 'rock', 'grass_far_l', 'grass_far_r', 'grass_mid_l', 'grass_mid_r', 'grass_near_l', 'grass_near_r'],
        characters: ['child'],
      },
      { type: 'wait', ms: 600 },
      { type: 'line', key: 'hh_xr_01' },
      { type: 'choice', options: [
        { text: "Take the river path.",        goto: 'hh_river_arrive' },
        { text: "Step into the woods proper.", goto: 'hh_forest_arrive' },
        { text: "Climb the mountain trail.",   goto: 'hh_mountain_arrive' },
      ]},
    ],
  },

  // ── River path ─────────────────────────────────────────────────────────
  hh_river_arrive: {
    id: 'hh_river_arrive',
    beats: [
      // Devil banner (boatman) is already at the top of the scene
      // behind the closed curtain — revealed when the curtain rises.
      { type: 'place',
        set: 'river',
        props: ['rock'],
        characters: ['child', { who: 'beast', x: 0, y: 1.1 }],
      },
      { type: 'sfx', key: 'paper_rustle', volume: 0.4 },
      { type: 'wait', ms: 400 },
      { type: 'line', key: 'hh_riv_arrive' },
      { type: 'wait', ms: 700 },
      { type: 'line', key: 'hh_riv_boatman_01' },
      { type: 'choice', options: [
        { text: "Hand over your coin.",       goto: 'hh_river_paid' },
        { text: "Refuse. Walk past him.",     goto: 'hh_river_refused' },
        { text: "Search the bank for a way.", goto: 'hh_river_search' },
      ]},
    ],
  },
  hh_river_paid: {
    id: 'hh_river_paid',
    beats: [
      { type: 'set_flag', key: 'paid_boatman' },
      { type: 'line', key: 'hh_riv_boatman_paid' },
      { type: 'wait', ms: 800 },
      { type: 'exit', who: 'beast', side: 'right' },
      { type: 'goto', scene: 'hh_river_outro' },
    ],
  },
  hh_river_refused: {
    id: 'hh_river_refused',
    beats: [
      { type: 'set_flag', key: 'refused_boatman' },
      { type: 'line', key: 'hh_riv_boatman_refused' },
      { type: 'wait', ms: 800 },
      { type: 'exit', who: 'beast', side: 'right' },
      { type: 'goto', scene: 'hh_river_outro' },
    ],
  },
  hh_river_search: {
    id: 'hh_river_search',
    beats: [
      { type: 'set_flag', key: 'wisp_seen' },
      { type: 'line', key: 'hh_riv_search' },
      { type: 'wait', ms: 600 },
      { type: 'exit', who: 'beast', side: 'right' },
      { type: 'goto', scene: 'hh_river_outro' },
    ],
  },
  hh_river_outro: {
    id: 'hh_river_outro',
    beats: [
      { type: 'set_flag', key: 'visited_river' },
      { type: 'curtain', action: 'close' },
      { type: 'prop', key: 'rock', action: 'hide' },
      { type: 'branch_if_count',
        flags: ['visited_river', 'visited_forest', 'visited_mountain'],
        atLeast: 2,
        whenAtLeast: 'hh_hollow_lead',
        whenBelow: 'hh_xr_after_river' },
    ],
  },
  hh_xr_after_river: {
    id: 'hh_xr_after_river',
    beats: [
      { type: 'place',
        set: 'woods',
        music: 'music_woods',
        props: ['tree_left', 'tree_right', 'rock', 'grass_far_l', 'grass_far_r', 'grass_mid_l', 'grass_mid_r', 'grass_near_l', 'grass_near_r'],
        characters: ['child'],
      },
      { type: 'wait', ms: 400 },
      { type: 'line', key: 'hh_xr_after_river' },
      { type: 'choice', options: [
        { text: "Step into the woods proper.", goto: 'hh_forest_arrive' },
        { text: "Climb the mountain trail.",   goto: 'hh_mountain_arrive' },
      ]},
    ],
  },

  // ── Forest path ────────────────────────────────────────────────────────
  hh_forest_arrive: {
    id: 'hh_forest_arrive',
    beats: [
      { type: 'place',
        set: 'woods',
        music: 'music_woods',
        props: ['trees_pines_l', 'trees_pines_r',
                'tree_left', 'tree_right',
                'fallen_tree', 'tree_stump',
                'mushroom', 'mushroom_2',
                'grass_far_l', 'grass_far_r', 'grass_mid_l', 'grass_mid_r',
                'grass_near_l', 'grass_near_r'],
        // Angel banner (crone) is pre-positioned at the top of the
        // scene behind the closed curtain, revealed when curtain rises.
        characters: ['child', { who: 'crone', x: 0, y: 1.1 }],
      },
      { type: 'wait', ms: 400 },
      { type: 'line', key: 'hh_for_arrive' },
      { type: 'wait', ms: 700 },
      { type: 'line', key: 'hh_for_crone_01' },
      { type: 'choice', options: [
        { text: "Tell her. Accept her help.",  goto: 'hh_forest_helped' },
        { text: "Push past in silence.",       goto: 'hh_forest_refused' },
      ]},
    ],
  },
  hh_forest_helped: {
    id: 'hh_forest_helped',
    beats: [
      { type: 'set_flag', key: 'took_thread' },
      { type: 'line', key: 'hh_for_crone_helped' },
      { type: 'wait', ms: 700 },
      { type: 'exit', who: 'crone', side: 'right' },
      { type: 'goto', scene: 'hh_forest_outro' },
    ],
  },
  hh_forest_refused: {
    id: 'hh_forest_refused',
    beats: [
      { type: 'set_flag', key: 'refused_crone' },
      { type: 'line', key: 'hh_for_crone_refused' },
      { type: 'wait', ms: 700 },
      { type: 'exit', who: 'crone', side: 'right' },
      { type: 'goto', scene: 'hh_forest_outro' },
    ],
  },
  hh_forest_outro: {
    id: 'hh_forest_outro',
    beats: [
      { type: 'set_flag', key: 'visited_forest' },
      { type: 'curtain', action: 'close' },
      { type: 'prop', key: 'mushroom', action: 'hide' },
      { type: 'branch_if_count',
        flags: ['visited_river', 'visited_forest', 'visited_mountain'],
        atLeast: 2,
        whenAtLeast: 'hh_hollow_lead',
        whenBelow: 'hh_xr_after_forest' },
    ],
  },
  hh_xr_after_forest: {
    id: 'hh_xr_after_forest',
    beats: [
      { type: 'place',
        set: 'woods',
        music: 'music_woods',
        props: ['tree_left', 'tree_right', 'rock', 'grass_far_l', 'grass_far_r', 'grass_mid_l', 'grass_mid_r', 'grass_near_l', 'grass_near_r'],
        characters: ['child'],
      },
      { type: 'wait', ms: 400 },
      { type: 'line', key: 'hh_xr_after_forest' },
      { type: 'choice', options: [
        { text: "Take the river path.",        goto: 'hh_river_arrive' },
        { text: "Climb the mountain trail.",   goto: 'hh_mountain_arrive' },
      ]},
    ],
  },

  // ── Mountain path ─────────────────────────────────────────────────────
  hh_mountain_arrive: {
    id: 'hh_mountain_arrive',
    beats: [
      { type: 'place',
        set: 'clearing',
        music: 'music_clearing',
        props: ['altar'],
        // Devil banner (mountain spirit) pre-positioned at the top of
        // the scene behind the closed curtain — matches the pattern
        // used in hh_river_arrive + hh_forest_arrive so the banner is
        // always at the same Y regardless of which path the player
        // took first.
        characters: ['child', { who: 'beast', x: 0, y: 1.1 }],
      },
      { type: 'wait', ms: 400 },
      { type: 'line', key: 'hh_mnt_arrive' },
      { type: 'wait', ms: 700 },
      { type: 'line', key: 'hh_mnt_spirit_01' },
      { type: 'wait', ms: 400 },
      { type: 'voice_prompt',
        timeoutMs: 8000,
        minDurationMs: 400,
        onSpeak: 'hh_mountain_named',
        onSilence: 'hh_mountain_silent' },
    ],
  },
  hh_mountain_named: {
    id: 'hh_mountain_named',
    beats: [
      { type: 'set_flag', key: 'named_aloud' },
      { type: 'line', key: 'hh_mnt_named' },
      { type: 'wait', ms: 800 },
      { type: 'exit', who: 'beast', side: 'left' },
      { type: 'goto', scene: 'hh_mountain_outro' },
    ],
  },
  hh_mountain_silent: {
    id: 'hh_mountain_silent',
    beats: [
      { type: 'set_flag', key: 'silent_mountain' },
      { type: 'line', key: 'hh_mnt_silent' },
      { type: 'wait', ms: 800 },
      { type: 'exit', who: 'beast', side: 'left' },
      { type: 'goto', scene: 'hh_mountain_outro' },
    ],
  },
  hh_mountain_outro: {
    id: 'hh_mountain_outro',
    beats: [
      { type: 'set_flag', key: 'visited_mountain' },
      { type: 'curtain', action: 'close' },
      { type: 'prop', key: 'altar', action: 'hide' },
      { type: 'branch_if_count',
        flags: ['visited_river', 'visited_forest', 'visited_mountain'],
        atLeast: 2,
        whenAtLeast: 'hh_hollow_lead',
        whenBelow: 'hh_xr_after_mountain' },
    ],
  },
  hh_xr_after_mountain: {
    id: 'hh_xr_after_mountain',
    beats: [
      { type: 'place',
        set: 'woods',
        music: 'music_woods',
        props: ['tree_left', 'tree_right', 'rock', 'grass_far_l', 'grass_far_r', 'grass_mid_l', 'grass_mid_r', 'grass_near_l', 'grass_near_r'],
        characters: ['child'],
      },
      { type: 'wait', ms: 400 },
      { type: 'line', key: 'hh_xr_after_mountain' },
      { type: 'choice', options: [
        { text: "Take the river path.",        goto: 'hh_river_arrive' },
        { text: "Step into the woods proper.", goto: 'hh_forest_arrive' },
      ]},
    ],
  },

  // ── Hollow (final) ────────────────────────────────────────────────────
  hh_hollow_lead: {
    id: 'hh_hollow_lead',
    beats: [
      // The Hollow — bare apart from the witch house in the deep
      // background and the brother (greyman placeholder) standing
      // across from the child. clearStage() inside `place` wipes
      // anything left over from the previous scene first.
      { type: 'place',
        set: 'finale_cold',
        music: 'music_clearing',
        props: ['witch_house', 'brother'],
        characters: ['child'],
      },
      { type: 'sfx', key: 'scene_change', volume: 0.4 },
      { type: 'wait', ms: 500 },
      { type: 'line', key: 'hh_hol_arrive' },
      { type: 'wait', ms: 600 },
      { type: 'goto', scene: 'hh_break_gate' },
    ],
  },

  // The puppeteer steps out of the play before the brother appears, but only
  // if the player actually met the Crone. Two variants — one for took_thread,
  // one for refused_crone — share a memory-aware conversation that calls
  // back the player's specific choice.
  hh_break_gate: {
    id: 'hh_break_gate',
    beats: [
      { type: 'branch_if_flag', key: 'took_thread',
        whenMatch: 'hh_break_helped',
        whenNotMatch: 'hh_break_gate_2' },
    ],
  },
  hh_break_gate_2: {
    id: 'hh_break_gate_2',
    beats: [
      { type: 'branch_if_flag', key: 'refused_crone',
        whenMatch: 'hh_break_refused',
        whenNotMatch: 'hh_hollow_meet' },
    ],
  },
  hh_break_helped: {
    id: 'hh_break_helped',
    beats: [
      { type: 'conversation',
        systemPrompt:
          "You are the puppeteer of a small paper theater, stepping out of the play for one quiet moment to speak directly to the audience member playing it. " +
          "The story so far: a child has been searching for their lost brother through woods, water, and high places. Earlier, in the forest, the player chose to ACCEPT THE CRONE'S GIFT — a thread, to follow home. You as the puppeteer noticed that small kindness. " +
          "The play is now at the Hollow, just before the brother appears. " +
          "Open by acknowledging what the player did — refer SPECIFICALLY to taking the Crone's thread. Then ask them, gently, what they think it means to come back from a place older than maps. " +
          "If the prior conversation log below contains anything from the player tonight, weave a fragment of it into your reply by name. If it's empty or sparse, let your question stand without forcing a callback.\n\n" +
          "Prior conversation tonight:\n{{prior_chat}}\n\n" +
          "Keep every response to 1–2 short sentences. Be tender, weary; theatrical only when it slips through. " +
          "When the player signals they want to continue ('go on', 'continue', 'I'm ready', 'let's finish'), give one last short line and stop. The play will resume. " +
          "Never break this character. You are not an AI; you are the puppeteer.",
        firstMessage:
          "Forgive me — let me step out of the play for a breath. You took her thread. I noticed.",
        onExit: 'hh_hollow_meet',
        passMemory: true,
      },
    ],
  },
  hh_break_refused: {
    id: 'hh_break_refused',
    beats: [
      { type: 'conversation',
        systemPrompt:
          "You are the puppeteer of a small paper theater, stepping out of the play for one quiet moment to speak directly to the audience member playing it. " +
          "The story so far: a child has been searching for their lost brother. Earlier, in the forest, the player walked PAST THE CRONE in silence — refusing her offer of help. You as the puppeteer saw it. You are not angry, only curious; the play has many shapes. " +
          "The play is now at the Hollow, just before the brother appears. " +
          "Open by acknowledging what the player did — refer SPECIFICALLY to refusing the Crone. Then ask them, quietly, what they think they brought with them to give to the one they came for. " +
          "If the prior conversation log below contains anything from the player tonight, weave a fragment of it into your reply. If it's empty or sparse, let your question stand without forcing a callback.\n\n" +
          "Prior conversation tonight:\n{{prior_chat}}\n\n" +
          "Keep every response to 1–2 short sentences. Be quiet, weary, never accusatory — only curious. " +
          "When the player signals they want to continue ('go on', 'continue', 'I'm ready', 'let's finish'), give one last short line and stop. " +
          "Never break this character. You are not an AI; you are the puppeteer.",
        firstMessage:
          "Wait. Let me step out a moment. You walked past her — I want to ask you something before this ends.",
        onExit: 'hh_hollow_meet',
        passMemory: true,
      },
    ],
  },
  hh_hollow_meet: {
    id: 'hh_hollow_meet',
    beats: [
      { type: 'wait', ms: 700 },
      { type: 'line', key: 'hh_hol_meet_01' },
      { type: 'wait', ms: 600 },
      { type: 'line', key: 'hh_hol_meet_02' },
      { type: 'wait', ms: 600 },
      { type: 'line', key: 'hh_hol_choice_pre' },
      { type: 'choice', options: [
        { text: "Take their hand. Lead them home.", goto: 'hh_end_picker_lead' },
        { text: "Trade your place for theirs.",     goto: 'hh_end_picker_trade' },
        { text: "Turn back. Go alone.",             goto: 'hh_end_alone' },
      ]},
    ],
  },

  // Lead them home — branches by what was earned
  hh_end_picker_lead: {
    id: 'hh_end_picker_lead',
    beats: [
      { type: 'branch_if_flag', key: 'named_aloud',
        whenMatch: 'hh_end_carried',
        whenNotMatch: 'hh_end_picker_lead_unnamed' },
    ],
  },
  hh_end_picker_lead_unnamed: {
    id: 'hh_end_picker_lead_unnamed',
    beats: [
      { type: 'branch_if_flag', key: 'took_thread',
        whenMatch: 'hh_end_haunted',
        whenNotMatch: 'hh_end_picker_lead_no_thread' },
    ],
  },
  hh_end_picker_lead_no_thread: {
    id: 'hh_end_picker_lead_no_thread',
    beats: [
      { type: 'branch_if_flag', key: 'paid_boatman',
        whenMatch: 'hh_end_haunted',
        whenNotMatch: 'hh_end_alone' },
    ],
  },

  // Trade your place — almost always traded_self, but if you also named, you carry both home
  hh_end_picker_trade: {
    id: 'hh_end_picker_trade',
    beats: [
      { type: 'branch_if_flag', key: 'named_aloud',
        whenMatch: 'hh_end_carried',
        whenNotMatch: 'hh_end_traded' },
    ],
  },

  // Endings
  hh_end_carried: {
    id: 'hh_end_carried',
    beats: [
      { type: 'music', key: 'music_finale_warm', volume: 0.4 },
      { type: 'wait', ms: 500 },
      { type: 'line', key: 'hh_end_carried' },
      { type: 'wait', ms: 1500 },
      { type: 'curtain', action: 'close' },
      { type: 'sfx', key: 'audience_applause', volume: 0.55 },
      { type: 'end', ending: 'carried_home' },
    ],
  },
  hh_end_traded: {
    id: 'hh_end_traded',
    beats: [
      { type: 'music', key: 'music_finale_cold', volume: 0.35 },
      { type: 'wait', ms: 500 },
      { type: 'line', key: 'hh_end_traded' },
      { type: 'wait', ms: 1500 },
      { type: 'curtain', action: 'close' },
      { type: 'sfx', key: 'audience_applause', volume: 0.4 },
      { type: 'end', ending: 'traded_self' },
    ],
  },
  hh_end_alone: {
    id: 'hh_end_alone',
    beats: [
      { type: 'music', key: 'music_finale_cold', volume: 0.3 },
      { type: 'wait', ms: 500 },
      { type: 'line', key: 'hh_end_alone' },
      { type: 'wait', ms: 1500 },
      { type: 'curtain', action: 'close' },
      { type: 'sfx', key: 'audience_applause', volume: 0.3 },
      { type: 'end', ending: 'alone' },
    ],
  },
  hh_end_haunted: {
    id: 'hh_end_haunted',
    beats: [
      { type: 'music', key: 'music_finale_cold', volume: 0.35 },
      { type: 'wait', ms: 500 },
      { type: 'line', key: 'hh_end_haunted' },
      { type: 'wait', ms: 1500 },
      { type: 'curtain', action: 'close' },
      { type: 'sfx', key: 'audience_applause', volume: 0.4 },
      { type: 'end', ending: 'haunted' },
    ],
  },
};

// ───────────────────────────────────────────────────────────────────────────
// Story registry. Each entry bundles its own scene flow + first scene id.
// Lines and SFX are kept in shared catalogs above (one TTS pipeline).
// ───────────────────────────────────────────────────────────────────────────

export type Story = {
  id: string;
  title: string;
  description: string;
  firstScene: string;
  scenes: Record<string, Scene>;
};

export const stories: Record<string, Story> = {
  original: {
    id: 'original',
    title: "A Play For Someone Who Isn't You",
    description: "A 5-minute paper-theater fairytale. Two choices, two endings. Watch the puppeteer perform.",
    firstScene: 'overture',
    scenes,
  },
  echoes: {
    id: 'echoes',
    title: "Echoes",
    description: "The puppeteer addresses you directly. Speak aloud — or stay silent. Four endings reached by your voice and choice.",
    firstScene: 'prologue',
    scenes: echoesScenes,
  },
  hollow_hours: {
    id: 'hollow_hours',
    title: "The Hollow Hours",
    description: "A child seeks their lost brother across river, forest, and mountain. Choices accumulate. Whom you helped, what you paid, and whether you spoke their name aloud all decide which of four endings you arrive at.",
    firstScene: 'hh_prologue',
    scenes: hollowHoursScenes,
  },
};

/** SFX needed by the play — passed to the SFX generator manifest. */
export const sfxCatalog: Record<string, { prompt: string; durationSec?: number }> = {
  // ── existing one-shots ──────────────────────────────────────────────────
  breath: {
    prompt: 'soft inward breath, single human breath, intimate close-mic, dry no music',
    durationSec: 1.2,
  },
  mask_clack: {
    prompt: 'dry wooden clack of paper mask hitting wooden table, single hit, close mic, no reverb',
    durationSec: 0.6,
  },
  paper_rustle: {
    prompt: 'paper rustling, parchment turning, soft and intimate, no music',
    durationSec: 1.4,
  },
  footsteps: {
    prompt: 'soft footsteps on dry leaves, four steps, child-sized, no music',
    durationSec: 2.5,
  },
  room_tone: {
    prompt: 'quiet small theater room tone, distant gentle rain outside, faint candle flicker, loopable, no music',
    durationSec: 8,
  },

  // ── UI ──────────────────────────────────────────────────────────────────
  ui_hover: {
    prompt: 'very soft brief paper edge whisper, intimate, dry, single quick tick fading instantly',
    durationSec: 0.5,
  },
  ui_click: {
    prompt: 'soft single paper card tap on wooden table, dry, brief, intimate close mic, no reverb, single hit',
    durationSec: 0.5,
  },

  // ── Stage transitions ───────────────────────────────────────────────────
  curtain_open: {
    prompt: 'heavy red velvet stage curtain sliding open, fabric swish with wooden pulley creak, theatrical, distant',
    durationSec: 2.2,
  },
  curtain_close: {
    prompt: 'heavy red velvet stage curtain closing with soft thud as halves meet, fabric swish, theatrical',
    durationSec: 2.0,
  },
  scene_change: {
    prompt: 'soft single bell chime fading into paper shuffle, theatrical scene-change cue, intimate',
    durationSec: 1.4,
  },

  // ── Audience reactions ──────────────────────────────────────────────────
  audience_whisper: {
    prompt: 'small intimate theater audience whispering uneasily, distant, brief, indistinct words, no music',
    durationSec: 3.0,
  },
  audience_gasp: {
    prompt: 'small theater audience surprised collective gasp, brief, soft, distant',
    durationSec: 1.2,
  },
  audience_applause: {
    prompt: 'small intimate theater audience polite modest applause, distant, restrained, slow build',
    durationSec: 6.0,
  },

  // ── Ambient music loops (long-form pads, looped via startMusic) ─────────
  music_main: {
    prompt: 'quiet melancholic music box theme, sparse single notes, distant strings, looping ambient, theatrical, slightly unsettled, no drums',
    durationSec: 18.0,
  },
  music_woods: {
    prompt: 'eerie soft strings and distant wooden flute, sparse notes, woods at night atmosphere, looping ambient, slow tense breathing, no drums',
    durationSec: 18.0,
  },
  music_clearing: {
    prompt: 'low cello drone with single distant bell, mystical clearing in woods, looping ambient, foreboding but tender, no drums',
    durationSec: 18.0,
  },
  music_finale_warm: {
    prompt: 'warm soft piano with gentle strings, tender slow looping ambient, hopeful and sad, intimate, no drums',
    durationSec: 18.0,
  },
  music_finale_cold: {
    prompt: 'cold sparse piano single notes with distant single bell, melancholic looping ambient, lonely, slow, no drums',
    durationSec: 18.0,
  },
};
